import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CanvasContext from '../../../context/CanvasContext';
import EditSidebarTabs from './EditSidebarTabs';
import ChoicesList from './ChoicesList';
import StyledSwitch from './StyledSwitch';
import UploadModal from './UploadModal';
import textStyles from './styles/EditSidebarText.module.css';
import settingsStyles from './styles/EditSidebarSettings.module.css';
import keyStyles from './styles/EditSidebarKeys.module.css';
import DeleteModal from './DeleteModal';
import useIsMobile from '../../../utils/useIsMobile';
import MediaPlayer from '../../../utils/MediaPlayer';
import { getMediaSrc } from '../../../utils/utils';
import TextEditor from '../../texteditor/TextEditor';
import { FaEye } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';

const EditSituation = ({ setRenderEditSidebar }) => {
    
    const {
        scenario,
        updateSituationAndNode,
        deleteSituationAndNode,
        selectedSituation,
        selectedNodeId,
        activeEditTab,
        keys,
        loading,
    } = useContext(CanvasContext);

    const navigate = useNavigate();

    
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [choices, setChoices] = useState([]);
    const [image, setImage] = useState(null);
    const [audio, setAudio] = useState(null);
    const [audioLabel, setAudioLabel] = useState("");
    const [group, setGroup] = useState(null);
    const [activeSubTab, setActiveSubTab] = useState('text');

    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [uploadType, setUploadType] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    
    const [isStart, setIsStart] = useState(false);
    const [isEnd, setIsEnd] = useState(false);
    const [isUserSubmitted, setIsUserSubmitted] = useState(false);
    const [isFreeResponse, setIsFreeResponse] = useState(false);
    const [isLogged, setIsLogged] = useState(false);
    const [autoplay, setAutoplay] = useState(false);

    
    const [grantsKeys, setGrantsKeys] = useState([]);
    const [isGrantingKey, setIsGrantingKey] = useState(false);

    const isMobile = useIsMobile();

    
    useEffect(() => {
        setTitle(selectedSituation.title || '');
        setText(selectedSituation.text || '');
        setIsStart(selectedSituation.isStart || false);
        setIsEnd(selectedSituation.isEnd || false);
        setIsUserSubmitted(selectedSituation.isUserSubmitted || false);
        setIsFreeResponse(selectedSituation.isFreeResponse || false);
        setIsLogged(selectedSituation.isLogged || false);
        setChoices(selectedSituation.choices || []);
        setImage(selectedSituation.image || null);
        setAudio(selectedSituation.audio || null);
        setAudioLabel(selectedSituation.audioLabel || "");
        setGroup(selectedSituation.situationGroup || null);
        setGrantsKeys(selectedSituation.grantsKeys || []);
        setAutoplay(selectedSituation.autoplay || false);
        
    }, [selectedSituation]);

    const openModal = (type) => {
        setUploadType(type);
        setIsModalOpen(true);
    };

    const handleTitleChange = (event) => {
        setTitle(event.target.value);
    };

    const handleTitleBlur = async () => {
        
        if (selectedSituation && selectedNodeId && title !== selectedSituation.title) {
            try {
                
                await updateSituationAndNode(selectedNodeId, { title: title });
                
            } catch (error) {
                console.error('Error updating situation title: ', error);
            }
        }
    };

    const handleAudioLabelChange = (event) => {
        setAudioLabel(event.target.value);
    };

    const handleAudioLabelBlur = async () => {
        if (loading || !selectedNodeId || audioLabel === selectedSituation?.audioLabel) return;
        try {
            await updateSituationAndNode(selectedNodeId, { audioLabel: audioLabel });
        } catch (error) {
            console.error('Error updating audio label: ', error);
        }
    };

    
    const handleRemoveMedia = async (mediaType) => { 
        if (loading || !selectedNodeId) return; 

        try {
            
            await updateSituationAndNode(selectedNodeId, { [mediaType]: null });
            
            console.log(`${mediaType} removed successfully.`);
        } catch (error) {
            console.error(`Error removing ${mediaType}:`, error);
            
        }
    };

    const handleTextEditorChange = (data) => {
        setText(data);
    };

    const handleTextEditorBlur = async () => {
        if (selectedSituation && selectedNodeId && text !== selectedSituation.text) {
            try {
                
                await updateSituationAndNode(selectedNodeId, { text: text });
            } catch (error) {
                console.error('Error updating situation text: ', error);
            }
        }
    };

    const handlePreviewSituation = () => {
        if (selectedSituation) {
            navigate(`/create/${scenario._id}/preview`, {state : {currentSituationId: selectedSituation?._id}});
        }
    };

    const handleSwitchChange = async (setting, value) => {
        if (loading || !selectedNodeId) return;
        try {
            let updates = { [setting]: value };
             
             if (setting === 'isStart' && value) updates.isEnd = false;
             else if (setting === 'isEnd' && value) updates.isStart = false;
             if (setting === 'isUserSubmitted' && value) updates.isFreeResponse = false;
             else if (setting === 'isFreeResponse' && value) updates.isUserSubmitted = false;

             
             

             
             const result = await updateSituationAndNode(selectedNodeId, updates);

             
             if (result && result.updatedSituation) {
                 const { isStart, isEnd, isUserSubmitted, isFreeResponse, isLogged, autoplay } = result.updatedSituation;
                 setIsStart(isStart ?? false); setIsEnd(isEnd ?? false); 
                 setIsUserSubmitted(isUserSubmitted ?? false); setIsFreeResponse(isFreeResponse ?? false);
                 setIsLogged(isLogged ?? false); setAutoplay(autoplay ?? false);
             }

        } catch (error) {
            console.error(`Error updating ${setting}: `, error);
        }
    };

    const handleDeleteSituation = () => {
        
        if (selectedSituation && selectedSituation._id) {
             setIsDeleteModalOpen(true); 
        } else {
             console.warn("Delete button clicked but no situation is selected.");
        }
    };

    const confirmDeleteSituation = async () => {
        
        if (!selectedSituation || !selectedSituation._id || loading) {
            console.error("Cannot delete situation: No situation selected or operation in progress.");
            setIsDeleteModalOpen(false); 
            return;
        }
        try {
            const situationIdToDelete = selectedSituation._id; 

            
            await deleteSituationAndNode(situationIdToDelete);

            setIsDeleteModalOpen(false); 

            
             if (isMobile && typeof setRenderEditSidebar === 'function') {
                 setRenderEditSidebar(false);
             }

        } catch (error) {
            console.error('Error during situation deletion process:', error);
            setIsDeleteModalOpen(false); 
            
        }
    };

    const cancelDeleteSituation = () => {
        setIsDeleteModalOpen(false);  
    };

    
    const handleAddKey = async (keyId) => {
        if (!selectedNodeId) return;
        try {
            const updatedGrantsKeys = [...grantsKeys, keyId];
            const result = await updateSituationAndNode(selectedNodeId, { grantsKeys: updatedGrantsKeys });
            if (result && result.updatedSituation) {
                setGrantsKeys(result.updatedSituation.grantsKeys || []); 
            }
            setIsGrantingKey(false); 
        } catch (error) {
            console.error('Error adding key:', error);
        }
    };

    
    const handleRemoveKey = async (keyId) => {
        if (!selectedNodeId) return;
        try {
            const updatedGrantsKeys = grantsKeys.filter(id => id !== keyId);
            const result = await updateSituationAndNode(selectedNodeId, { grantsKeys: updatedGrantsKeys });
            if (result && result.updatedSituation) {
                setGrantsKeys(result.updatedSituation.grantsKeys || []);
            }
        } catch (error) {
            console.error('Error removing key:', error);
        }
    };

    return (
        <>
            <EditSidebarTabs editType="situation" />

            {activeEditTab === '1' && (
                <>
                    {/* Subtabs */}
                    <div className={textStyles.subTabsContainer}>
                        <div
                            className={textStyles.iconItem}
                            onClick={handlePreviewSituation}
                            data-tooltip-id="preview_tooltip"
                            data-tooltip-content="Preview Situation"
                        >
                            <FaEye />
                        </div>
                        <div
                            className={`${textStyles.subTab} ${
                                activeSubTab === 'text' ? textStyles.activeSubTab : ''
                            }`}
                            onClick={() => setActiveSubTab('text')}
                        >
                            Text
                        </div>
                        <div
                            className={`${textStyles.subTab} ${
                                activeSubTab === 'media' ? textStyles.activeSubTab : ''
                            }`}
                            onClick={() => setActiveSubTab('media')}
                        >
                            Media
                        </div>
                    </div>
                    <div className={textStyles.textContainer}>
                        {/* Subtab Content */}
                        {activeSubTab === 'text' && (
                            <>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={handleTitleChange}
                                    onBlur={handleTitleBlur}
                                    className={textStyles.titleInput}
                                />
                                <TextEditor
                                    initialData={text}
                                    onChange={handleTextEditorChange}
                                    onBlur={handleTextEditorBlur}
                                />
                            </>
                        )}

                        {activeSubTab === 'media' && (
                            <>
                                {image && (
                                    <div className={textStyles.mediaPreview}>
                                        <img src={image} alt="Uploaded" className={textStyles.imagePreview} />
                                    </div>
                                )}
                
                                <div className={textStyles.buttonGroup}>
                                    {/* Button to OPEN the modal */}
                                    <button
                                        onClick={() => openModal('image')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                        disabled={loading} 
                                    >
                                        {image ? 'Change Image' : 'Upload Image'}
                                    </button>
                                    {/* Button to REMOVE the media */}
                                    {image && (
                                        <button
                                            onClick={() => handleRemoveMedia('image')} 
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                            disabled={loading} 
                                        >
                                            Remove Image
                                        </button>
                                    )}
                                </div>
                
                                {audio && (
                                    <>
                                        {/* Audio Label Input */}
                                        <input
                                            type="text"
                                            value={audioLabel}
                                            onChange={handleAudioLabelChange}
                                            onBlur={handleAudioLabelBlur}
                                            className={textStyles.titleInput}
                                            disabled={loading} 
                                        />
                                        {/* Audio/Video Preview */}
                                        <div className={textStyles.mediaPreview}>
                                            <MediaPlayer
                                                src={getMediaSrc(audio)}
                                                autoplay={false}
                                                key={getMediaSrc(audio)} 
                                            />
                                        </div>
                                    </>
                                )}
                
                                <div className={textStyles.buttonGroup}>
                                    {/* Button to OPEN the modal */}
                                    <button
                                        onClick={() => openModal('audiovideo')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                        disabled={loading} 
                                    >
                                        {/* Check audio type if 'audio' can be an object */}
                                        {audio ? 'Change Audio/Video' : 'Upload Audio/Video'}
                                    </button>
                                    {/* Button to REMOVE the media */}
                                    {audio && (
                                        <button
                                            onClick={() => handleRemoveMedia('audio')} 
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                            disabled={loading} 
                                        >
                                            {/* Check audio type if 'audio' can be an object */}
                                            Remove Audio/Video
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}

            {activeEditTab === '2' && choices && (
                <ChoicesList choices={choices} setChoices={setChoices} />
            )}

            {activeEditTab === '3' && (
                <div className={settingsStyles.settingsContainer}>
                    <div className={settingsStyles.settingItem}>
                        <label>Starting Situation</label>
                        <StyledSwitch
                            onChange={(value) => handleSwitchChange('isStart', value)}
                            checked={isStart}
                            className={settingsStyles.switch}
                            disabled={loading}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Ending Situation</label>
                        <StyledSwitch
                            onChange={(value) => handleSwitchChange('isEnd', value)}
                            checked={isEnd}
                            disabled={(choices && choices.length > 0) || loading}
                            className={settingsStyles.switch}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Open User Submission</label>
                        <StyledSwitch
                            onChange={(value) => handleSwitchChange('isUserSubmitted', value)}
                            checked={isUserSubmitted}
                            className={settingsStyles.switch}
                            disabled={loading}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Free-Response Matching</label>
                        <StyledSwitch
                            onChange={(value) => handleSwitchChange('isFreeResponse', value)}
                            checked={isFreeResponse}
                            className={settingsStyles.switch}
                            disabled={loading}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Track User Decisions</label>
                        <StyledSwitch
                            onChange={(value) => handleSwitchChange('isLogged', value)}
                            checked={isLogged}
                            className={settingsStyles.switch}
                            disabled={loading}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Autoplay Media</label>
                        <StyledSwitch
                            onChange={(value) => handleSwitchChange('autoplay', value)}
                            checked={autoplay}
                            className={settingsStyles.switch}
                            disabled={loading}
                        />
                    </div>
                    {/* Delete Situation Button */}
                    <div className={settingsStyles.deleteContainer}>
                        <button onClick={handleDeleteSituation} className={settingsStyles.deleteButton}>
                            Delete Situation
                        </button>
                    </div>
                </div>
            )}

            {activeEditTab === '4' && (
                <div className={keyStyles.keysContainer}>
                    <h3>Keys Given</h3>
                    {grantsKeys.length > 0 ? (
                        <ul className={keyStyles.grantedKeysList}>
                            {grantsKeys.map(keyId => {
                                const key = keys.find(k => k._id === keyId);
                                return (
                                    <li key={keyId} className={keyStyles.grantedKeyItem}>
                                        <span>{key ? key.name : 'Unknown Key'}</span>
                                        <button
                                            onClick={() => handleRemoveKey(keyId)}
                                            className={keyStyles.removeKeyButton}
                                        >
                                            Remove
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p>No keys are given to the user who reaches this situation.</p>
                    )}
                    {!isGrantingKey && (
                        <button
                            onClick={() => setIsGrantingKey(true)}
                            className={keyStyles.grantKeyButton}
                        >
                            Give Key
                        </button>
                    )}

                    {isGrantingKey && (
                        <div className={keyStyles.availableKeysContainer}>
                            <h4>Select a key to give:</h4>
                            <ul className={keyStyles.availableKeysList}>
                                {keys
                                    .filter(k => !grantsKeys.includes(k._id))
                                    .map(key => (
                                        <li
                                            key={key._id}
                                            className={keyStyles.availableKeyItem}
                                            onClick={() => handleAddKey(key._id)}
                                        >
                                            <div className={keyStyles.keyName}>{key.name}</div>
                                            <div className={keyStyles.keyDescription}>{key.description}</div>
                                        </li>
                                    ))}
                            </ul>
                            <button
                                onClick={() => setIsGrantingKey(false)}
                                className={keyStyles.cancelGrantButton}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            )}


            {/* Modals */}
            {isModalOpen && (
                <UploadModal uploadType={uploadType} setIsModalOpen={setIsModalOpen} />
            )}

            {isDeleteModalOpen && (
                <DeleteModal
                    onConfirm={confirmDeleteSituation}
                    onCancel={cancelDeleteSituation}
                    type={'Situation'}
                />
            )}

            <Tooltip
                id="preview_tooltip"
                place="left"
                type="dark"
                effect="solid"
                style={{ zIndex: 9999 }}
            />
        </>
    );
};

export default EditSituation;
